import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  Typography,
  Chip,
  Divider,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import useApi from "../../hooks/useApi";

const SurveyViewerModal = ({ survey, open, onClose }) => {
  const [fullSurvey, setFullSurvey] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const language = useSelector((state) => state.authentication.language);
  const isFrench = String(language || "")
    .toLowerCase()
    .startsWith("fr");
  const { postData } = useApi();

  const getLocalizedField = (entity, englishKey, frenchKey, fallback = "") => {
    if (!entity || typeof entity !== "object") return fallback;

    const englishValue = entity[englishKey];
    const frenchValue = entity[frenchKey];

    if (isFrench) {
      return frenchValue || englishValue || fallback;
    }

    return englishValue || frenchValue || fallback;
  };

  const getLocalizedQuestionText = (question) => {
    if (!question || typeof question !== "object") return "";

    if (isFrench) {
      return (
        question.text_fr ||
        question.text ||
        question.name_fr ||
        question.name ||
        ""
      );
    }

    return (
      question.text ||
      question.text_fr ||
      question.name ||
      question.name_fr ||
      ""
    );
  };

  const getLocalizedOptionText = (option) => {
    if (!option || typeof option !== "object") return "";

    if (isFrench) {
      return option.name_fr || option.name || option.label || "";
    }

    return option.name || option.name_fr || option.label || "";
  };

  // Fetch full survey data if survey changes and has ID
  useEffect(() => {
    if (!open || !survey) {
      return;
    }

    const surveyId = survey.id || survey._id || survey.surveyId;

    if (surveyId && !survey.pages) {
      setIsLoading(true);
      postData({
        entityName: "Survey",
        action: "get",
        data: { id: surveyId },
        pipeline: "full",
      })
        .then((result) => {
          if (result?.isSuccessful && result.data) {
            setFullSurvey(result.data);
          } else if (result?.data) {
            // If data exists but not marked successful, use it anyway
            setFullSurvey(result.data);
          }
        })
        .catch((err) => {
          console.error("Error fetching survey:", err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else if (survey?.pages) {
      // Survey already has full structure
      setFullSurvey(survey);
    }
  }, [survey, open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!fullSurvey || !fullSurvey.pages) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
      >
        <DialogContent
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 300,
          }}
        >
          {isLoading ? (
            <CircularProgress />
          ) : (
            <Typography>No survey data available</Typography>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  const surveData = fullSurvey;
  const sortedPages = [...(surveData.pages || [])].sort(
    (a, b) => (a.pageNumber || 0) - (b.pageNumber || 0),
  );

  const getSectionsForPage = (pageId) => {
    return [...(surveData.sections || [])]
      .filter((s) => s.surveyPageId === pageId)
      .sort((a, b) => (a.sectionNumber || 0) - (b.sectionNumber || 0));
  };

  const getQuestionsForSection = (sectionId) => {
    return [...(surveData.questions || [])]
      .filter((q) => q.surveySectionId === sectionId)
      .sort((a, b) => (a.questionNumber || 0) - (b.questionNumber || 0));
  };

  const getQuestionTypeLabel = (type) => {
    const typeMap = {
      text: "Text",
      number: "Number",
      currency: "Currency",
      matrix: "Matrix",
      multiselect: "Multi-Select",
      radio: "Radio",
      checkbox: "Checkbox",
      date: "Date",
      time: "Time",
      image: "Image",
      signature: "Signature",
    };
    return typeMap[type] || type;
  };

  const getTypeColor = (type) => {
    const colorMap = {
      text: "default",
      number: "info",
      currency: "info",
      matrix: "warning",
      multiselect: "success",
      radio: "success",
      checkbox: "success",
      date: "info",
      time: "info",
      image: "secondary",
      signature: "secondary",
    };
    return colorMap[type] || "default";
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: "100%", maxWidth: "800px", maxHeight: "80vh" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">
          Survey: {getLocalizedField(fullSurvey, "name", "name_fr")}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ overflowY: "auto" }}>
        <Stack
          spacing={3}
          sx={{ paddingTop: 1 }}
        >
          {sortedPages.map((page, pageIdx) => (
            <Box key={page.id || pageIdx}>
              {/* Page Header */}
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#1976d2",
                  marginBottom: 2,
                  paddingBottom: 1,
                  borderBottom: "2px solid #1976d2",
                }}
              >
                {getLocalizedField(page, "name", "name_fr")}
              </Typography>

              {/* Sections in Page */}
              <Stack
                spacing={2}
                sx={{ marginLeft: 2 }}
              >
                {getSectionsForPage(page.id || page._id).map(
                  (section, sectionIdx) => (
                    <Box key={section.id || sectionIdx}>
                      {/* Section Header */}
                      <Box sx={{ marginBottom: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            alignItems: "center",
                            marginBottom: 1,
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600, color: "#333" }}
                          >
                            {getLocalizedField(section, "name", "name_fr")}
                          </Typography>
                          {section.skuMatrix &&
                            section.skuMatrix.length > 0 && (
                              <Chip
                                label="SKU"
                                size="small"
                                color="error"
                                variant="outlined"
                              />
                            )}
                        </Box>

                        {/* Text Section Content */}
                        {section.sectionType === "text" &&
                          (section.text || section.text_fr) && (
                            <Box
                              sx={{
                                padding: 1.5,
                                backgroundColor: "#f5f5f5",
                                borderLeft: "4px solid #ff9800",
                                borderRadius: 1,
                                marginBottom: 2,
                                "& p": { margin: "0.5rem 0", lineHeight: 1.6 },
                                "& ul, & ol": {
                                  paddingLeft: "1.5rem",
                                  margin: "0.5rem 0",
                                },
                                "& li": { marginBottom: "0.25rem" },
                                "& strong": { fontWeight: 600 },
                                "& em": { fontStyle: "italic" },
                                "& a": {
                                  color: "#1976d2",
                                  textDecoration: "none",
                                },
                              }}
                              dangerouslySetInnerHTML={{
                                __html: getLocalizedField(
                                  section,
                                  "text",
                                  "text_fr",
                                ),
                              }}
                            />
                          )}
                      </Box>

                      {/* Questions in Section */}
                      {getQuestionsForSection(section.id || section._id)
                        .length > 0 && (
                        <Stack
                          spacing={1.5}
                          sx={{ marginLeft: 1 }}
                        >
                          {getQuestionsForSection(
                            section.id || section._id,
                          ).map((question, qIdx) => (
                            <Box
                              key={question.id || qIdx}
                              sx={{
                                padding: 1,
                                backgroundColor: "#fafafa",
                                border: "1px solid #e0e0e0",
                                borderRadius: 1,
                              }}
                            >
                              {/* Question Text with Type Badge */}
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  alignItems: "flex-start",
                                  marginBottom: 0.5,
                                }}
                              >
                                <Chip
                                  label={getQuestionTypeLabel(
                                    question.questionType,
                                  )}
                                  size="small"
                                  color={getTypeColor(question.questionType)}
                                  variant="outlined"
                                  sx={{ minWidth: "80px" }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    flex: 1,
                                    fontWeight: 500,
                                    color: "#333",
                                    lineHeight: 1.5,
                                  }}
                                >
                                  {getLocalizedQuestionText(question)}
                                </Typography>
                                {question.isRequired && (
                                  <Chip
                                    label="Required"
                                    size="small"
                                    color="warning"
                                    variant="filled"
                                    sx={{ height: "24px" }}
                                  />
                                )}
                              </Box>

                              {/* Question Options */}
                              {question.options &&
                                question.options.length > 0 && (
                                  <Box sx={{ marginTop: 1, marginLeft: 1 }}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        display: "block",
                                        marginBottom: 0.5,
                                      }}
                                    >
                                      Options:
                                    </Typography>
                                    <Stack spacing={0.25}>
                                      {question.options.map(
                                        (option, optIdx) => (
                                          <Typography
                                            key={optIdx}
                                            variant="caption"
                                            sx={{
                                              color: "#666",
                                              paddingLeft: 1,
                                            }}
                                          >
                                            • {getLocalizedOptionText(option)}
                                          </Typography>
                                        ),
                                      )}
                                    </Stack>
                                  </Box>
                                )}

                              {/* Min/Max for Numeric Questions */}
                              {["number", "currency"].includes(
                                question.questionType,
                              ) &&
                                (question.minValue !== null ||
                                  question.maxValue !== null) && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: "block",
                                      marginTop: 0.5,
                                      color: "#999",
                                    }}
                                  >
                                    Range:{" "}
                                    {question.minValue !== null
                                      ? question.minValue
                                      : "—"}{" "}
                                    to{" "}
                                    {question.maxValue !== null
                                      ? question.maxValue
                                      : "—"}
                                  </Typography>
                                )}
                            </Box>
                          ))}
                        </Stack>
                      )}

                      {/* Divider between sections */}
                      {sectionIdx <
                        getSectionsForPage(page.id || page._id).length - 1 && (
                        <Divider sx={{ marginTop: 2 }} />
                      )}
                    </Box>
                  ),
                )}
              </Stack>

              {/* Divider between pages */}
              {pageIdx < sortedPages.length - 1 && (
                <Divider sx={{ marginTop: 3, marginBottom: 2 }} />
              )}
            </Box>
          ))}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          variant="contained"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SurveyViewerModal;
